import React from "react";
import useRouter from "../../hooks/router";

const ACCENT     = "#00c9a7";
const ACCENT2    = "#f7c94b";
const DARK_BG    = "#111e27";
const CARD_BG    = "#1a3347";
const NAV_BG     = "#1a3a4a";
const TEXT_MAIN  = "#e8f0f4";
const TEXT_MUTED = "#8fafc4";
const BORDER     = "rgba(255,255,255,0.1)";

const Check = () => (
    <span style={{ color: ACCENT }} className="mr-2 font-bold">✓</span>
);

const plans = [
    {
        name: "Starter",
        price: "Free",
        period: "forever",
        description: "Perfect for small clubs and new organizations just getting started.",
        highlight: false,
        cta: "Get Started Free",
        features: [
            "Up to 25 referees",
            "Up to 50 games/month",
            "Game assignment",
            "Email notifications",
            "Referee calendar",
            "Basic reporting",
        ],
        missing: [
            "Payment processing",
            "Text notifications",
            "CSV import",
            "Priority support",
        ],
    },
    {
        name: "Pro",
        price: "$49",
        period: "per month",
        description: "For growing organizations that need payments, imports, and full automation.",
        highlight: true,
        badge: "Most Popular",
        cta: "Start Free Trial",
        features: [
            "Unlimited referees",
            "Unlimited games",
            "Game assignment",
            "Email + text notifications",
            "Referee calendar",
            "Full reporting & analytics",
            "Payment processing + 1099s",
            "CSV/spreadsheet import",
            "Priority email support",
        ],
        missing: [],
    },
    {
        name: "League",
        price: "$149",
        period: "per month",
        description: "Multi-organization management for leagues, associations, and governing bodies.",
        highlight: false,
        cta: "Contact Sales",
        features: [
            "Everything in Pro",
            "Multiple organizations",
            "Cross-org referee sharing",
            "Advanced payment splits",
            "Dedicated account manager",
            "Custom onboarding",
            "SLA + phone support",
            "API access",
        ],
        missing: [],
    },
];

const faqs = [
    {
        q: "Is there really a free plan?",
        a: "Yes — the Starter plan is free forever with no credit card required. It's designed for small clubs that just need basic assignment features.",
    },
    {
        q: "Can I switch plans later?",
        a: "Absolutely. You can upgrade, downgrade, or cancel anytime from your account settings. Upgrades take effect immediately.",
    },
    {
        q: "How does payment processing work?",
        a: "We partner with Stripe for secure payment processing. Referees can receive payouts directly to their bank accounts, and we handle 1099 generation automatically at year-end.",
    },
    {
        q: "Do referees need to pay anything?",
        a: "No — referees use RefAlign completely free. Only organization admins and assigners need a plan.",
    },
    {
        q: "What counts as a 'game'?",
        a: "Any scheduled match or event added to the platform. Games are counted per calendar month and reset at the start of each billing period.",
    },
    {
        q: "Is my data secure?",
        a: "Yes. All data is encrypted in transit and at rest. We use Firebase (Google Cloud) infrastructure with 99.9% uptime SLA.",
    },
];

const Pricing = () => {
    const { handleNavigation } = useRouter();

    return (
        <div style={{ background: DARK_BG }} className="font-outfit min-h-screen flex flex-col">
            {/* Nav */}
            <nav style={{ background: NAV_BG }} className="w-full px-6 py-4 flex items-center justify-between shadow-md sticky top-0 z-50">
                <img
                    src="/logo.svg" alt="RefAlign"
                    className="h-9 cursor-pointer"
                    onClick={() => handleNavigation("/")}
                />
                <div className="flex gap-3">
                    <button
                        onClick={() => handleNavigation("/blog")}
                        style={{ color: TEXT_MUTED, border: `1px solid ${BORDER}` }}
                        className="px-4 py-2 rounded-lg text-sm hover:bg-white hover:bg-opacity-5 transition-all"
                    >
                        Blog
                    </button>
                    <button
                        onClick={() => handleNavigation("/signup")}
                        style={{ background: ACCENT, color: "#0d2230" }}
                        className="px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all"
                    >
                        Get Started Free
                    </button>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-20 pb-12 px-6 text-center">
                <div
                    style={{ background: "rgba(0,201,167,0.12)", color: ACCENT, border: "1px solid rgba(0,201,167,0.3)" }}
                    className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6"
                >
                    Simple, transparent pricing
                </div>
                <h1 style={{ color: TEXT_MAIN }} className="text-5xl font-bold mb-4">
                    Plans for every organization
                </h1>
                <p style={{ color: TEXT_MUTED }} className="text-lg max-w-xl mx-auto">
                    Start free. Scale as you grow. No hidden fees, no long-term contracts.
                </p>
            </section>

            {/* Plans */}
            <section className="px-6 pb-24 max-w-6xl mx-auto w-full">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan) => (
                        <div
                            key={plan.name}
                            style={{
                                background: plan.highlight ? "linear-gradient(145deg, #1e4060, #1a3347)" : CARD_BG,
                                border: plan.highlight ? `2px solid ${ACCENT}` : `1px solid ${BORDER}`,
                                position: "relative",
                            }}
                            className="rounded-2xl p-7 flex flex-col shadow-xl"
                        >
                            {plan.badge && (
                                <div
                                    style={{ background: ACCENT, color: "#0d2230" }}
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                                >
                                    {plan.badge}
                                </div>
                            )}

                            <div className="mb-6">
                                <h3 style={{ color: TEXT_MAIN }} className="text-lg font-bold mb-1">{plan.name}</h3>
                                <p style={{ color: TEXT_MUTED }} className="text-sm mb-4">{plan.description}</p>
                                <div className="flex items-end gap-1">
                                    <span style={{ color: TEXT_MAIN }} className="text-4xl font-black">{plan.price}</span>
                                    {plan.period && (
                                        <span style={{ color: TEXT_MUTED }} className="text-sm mb-1.5">/{plan.period}</span>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={() => handleNavigation("/signup")}
                                style={
                                    plan.highlight
                                        ? { background: ACCENT, color: "#0d2230" }
                                        : { border: `1px solid ${BORDER}`, color: TEXT_MAIN }
                                }
                                className="w-full py-3 rounded-xl text-sm font-bold hover:opacity-90 transition-all mb-6"
                            >
                                {plan.cta}
                            </button>

                            <div className="flex flex-col gap-2.5 flex-1">
                                {plan.features.map((f) => (
                                    <div key={f} className="flex items-start text-sm" style={{ color: TEXT_MUTED }}>
                                        <Check />{f}
                                    </div>
                                ))}
                                {plan.missing.map((f) => (
                                    <div key={f} className="flex items-start text-sm opacity-30" style={{ color: TEXT_MUTED }}>
                                        <span className="mr-2">✕</span>{f}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Enterprise callout */}
                <div
                    style={{ background: "rgba(247,201,75,0.07)", border: `1px solid rgba(247,201,75,0.2)` }}
                    className="mt-8 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4"
                >
                    <div>
                        <h3 style={{ color: ACCENT2 }} className="font-bold text-lg mb-1">Need something custom?</h3>
                        <p style={{ color: TEXT_MUTED }} className="text-sm">State associations, national federations, and large multi-sport orgs — let&apos;s talk.</p>
                    </div>
                    <a
                        href="mailto:sales@refalign.com"
                        style={{ background: ACCENT2, color: "#0d2230", whiteSpace: "nowrap" }}
                        className="px-6 py-3 rounded-xl text-sm font-bold hover:opacity-90 transition-all shrink-0"
                    >
                        Contact Sales →
                    </a>
                </div>
            </section>

            {/* FAQ */}
            <section style={{ background: "#0f1e29" }} className="py-20 px-6">
                <div className="max-w-3xl mx-auto">
                    <h2 style={{ color: TEXT_MAIN }} className="text-3xl font-bold text-center mb-12">
                        Frequently asked questions
                    </h2>
                    <div className="flex flex-col gap-6">
                        {faqs.map((faq) => (
                            <div
                                key={faq.q}
                                style={{ border: `1px solid ${BORDER}` }}
                                className="rounded-xl p-6"
                            >
                                <h3 style={{ color: TEXT_MAIN }} className="font-semibold mb-2 text-sm">{faq.q}</h3>
                                <p style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section
                style={{ background: "linear-gradient(135deg, #00c9a7 0%, #0091a7 100%)" }}
                className="py-16 px-6 text-center"
            >
                <h2 className="text-3xl font-bold text-white mb-3">Ready to get started?</h2>
                <p className="text-white opacity-90 mb-8 text-lg">Free plan. No credit card. Up and running in minutes.</p>
                <button
                    onClick={() => handleNavigation("/signup")}
                    style={{ background: "#0d2230", color: TEXT_MAIN }}
                    className="px-10 py-4 rounded-xl font-bold hover:opacity-90 transition-all shadow-xl"
                >
                    Get Started Free →
                </button>
            </section>

            {/* Footer */}
            <footer style={{ background: "#0a1820", borderTop: `1px solid ${BORDER}` }} className="py-6 px-6">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <p style={{ color: TEXT_MUTED }} className="text-xs">© 2026 RefAlign, Inc. All rights reserved.</p>
                    <button
                        onClick={() => handleNavigation("/")}
                        style={{ color: TEXT_MUTED }}
                        className="text-xs hover:text-white transition-colors"
                    >
                        ← Back to home
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default Pricing;
